/**
 * medi420-animations.js
 * Animation engine for medi420.de design uplift.
 *
 * Modules:
 *   1. Scroll Reveal     – auto-applies to .section-wrapper + manual [data-reveal]
 *   2. Header Scroll     – toggles .header-scrolled for backdrop-blur
 *   3. Hero Parallax     – subtle translateY on .hero__image
 *   4. Count-Up          – animated number counters on [data-count-to]
 *   5. Condition Matcher – custom element <condition-matcher-component>
 *
 * No dependencies · ES module · respects prefers-reduced-motion
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* --------------------------------------------------------------------------
   1. Scroll Reveal (IntersectionObserver)
   – Auto-observe every .section-wrapper (threshold 0.1)
   – Also observe any [data-reveal] for manual overrides
   – On intersect: add .is-visible, unobserve (fire once)
   – CSS handles stagger via nth-child transition-delays
   -------------------------------------------------------------------------- */
function initScrollReveal() {
  // Collect both auto-apply sections and manual data-reveal elements
  const sections = document.querySelectorAll('.section-wrapper');
  const manuals = document.querySelectorAll('[data-reveal]');
  const allTargets = new Set([...sections, ...manuals]);

  if (!allTargets.size) return;

  // Reduced-motion: skip animation, show everything immediately
  if (REDUCED_MOTION) {
    allTargets.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1 }
  );

  allTargets.forEach((el) => observer.observe(el));
}

/* --------------------------------------------------------------------------
   2. Header Scroll Effect
   – Adds .header-scrolled to .header-section when scrollY > 50
   – Uses passive scroll listener for performance
   – Debounced via requestAnimationFrame to avoid layout thrash
   -------------------------------------------------------------------------- */
function initHeaderScroll() {
  const header = document.querySelector('.header-section');
  if (!header) return;

  let ticking = false;

  function updateHeader() {
    const scrolled = window.scrollY > 50;
    header.classList.toggle('header-scrolled', scrolled);
    ticking = false;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    },
    { passive: true }
  );

  // Set initial state (page might be reloaded mid-scroll)
  updateHeader();
}

/* --------------------------------------------------------------------------
   3. Hero Parallax (subtle)
   – Translates .hero__image at 0.3× scroll speed
   – Only active while the hero wrapper is in viewport
   – Disabled when prefers-reduced-motion is set
   – Uses passive scroll listener + rAF for smooth performance
   -------------------------------------------------------------------------- */
function initHeroParallax() {
  if (REDUCED_MOTION) return;

  const heroWrapper = document.querySelector('.hero-wrapper');
  if (!heroWrapper) return;

  const heroImage = heroWrapper.querySelector('.hero__image');
  if (!heroImage) return;

  const PARALLAX_FACTOR = 0.3;
  let isInView = false;
  let ticking = false;

  // Track viewport intersection so we only compute on visible hero
  const observer = new IntersectionObserver(
    ([entry]) => {
      isInView = entry.isIntersecting;
      // Reset transform when hero leaves viewport
      if (!isInView) {
        heroImage.style.transform = '';
      }
    },
    { threshold: 0 }
  );
  observer.observe(heroWrapper);

  function updateParallax() {
    if (!isInView) {
      ticking = false;
      return;
    }

    const rect = heroWrapper.getBoundingClientRect();
    const heroHeight = rect.height;
    // scrollProgress: 0 when hero top at viewport bottom, 1 when hero bottom at viewport top
    const scrollProgress = -rect.top / (heroHeight + window.innerHeight);
    const offset = scrollProgress * PARALLAX_FACTOR * heroHeight;

    heroImage.style.transform = `translateY(${offset.toFixed(1)}px)`;
    ticking = false;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    },
    { passive: true }
  );
}

/* --------------------------------------------------------------------------
   4. Count-Up Animation
   – Animates [data-count-to] elements from 0 to target
   – Supports decimals and custom separators (data-count-separator)
   – Eased with ease-out-quart for a satisfying deceleration
   -------------------------------------------------------------------------- */
function easeOutQuart(t) {
  return 1 - Math.pow(1 - t, 4);
}

function animateCount(el) {
  const target = parseFloat(el.dataset.countTo);
  if (Number.isNaN(target)) return;

  const separator = el.dataset.countSeparator ?? '.';
  const decimals = (String(target).split('.')[1] || '').length;
  const duration = 1500; // ms
  let start = null;

  if (REDUCED_MOTION) {
    el.textContent = formatNumber(target, decimals, separator);
    return;
  }

  function formatNumber(value, dec, sep) {
    const fixed = value.toFixed(dec);
    if (sep === ',') {
      // German locale: thousands with '.', decimals with ','
      const [intPart, decPart] = fixed.split('.');
      const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return decPart ? withThousands + ',' + decPart : withThousands;
    }
    return fixed;
  }

  function step(timestamp) {
    if (!start) start = timestamp;
    const elapsed = timestamp - start;
    const progress = Math.min(elapsed / duration, 1);
    const current = target * easeOutQuart(progress);

    el.textContent = formatNumber(current, decimals, separator);

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

function initCountUp() {
  const els = document.querySelectorAll('[data-count-to]');
  if (!els.length) return;

  if (REDUCED_MOTION) {
    els.forEach(animateCount);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15 }
  );

  els.forEach((el) => observer.observe(el));
}

/* --------------------------------------------------------------------------
   5. Condition Matcher Tabs (Custom Element)
   – Tab component with aria support and crossfade transitions
   -------------------------------------------------------------------------- */
class ConditionMatcherComponent extends HTMLElement {
  connectedCallback() {
    this._tabs = this.querySelectorAll('[data-tab-target]');
    this._panels = this.querySelectorAll('[data-tab-panel]');

    if (!this._tabs.length || !this._panels.length) return;

    this._tabs.forEach((tab) => {
      tab.addEventListener('click', this._onTabClick.bind(this));
    });

    // Ensure initial state is correct
    this._activateTab(this._tabs[0], false);
  }

  _onTabClick(event) {
    const tab = event.currentTarget;
    this._activateTab(tab, true);
  }

  _activateTab(activeTab, animate) {
    const targetId = activeTab.dataset.tabTarget;

    // Update tabs
    this._tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('condition-matcher__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update panels
    this._panels.forEach((panel) => {
      const isTarget = panel.dataset.tabPanel === targetId;
      panel.setAttribute('aria-hidden', String(!isTarget));
      panel.classList.toggle('is-active', isTarget);

      if (isTarget && animate && !REDUCED_MOTION) {
        // Trigger crossfade by forcing reflow
        panel.style.opacity = '0';
        // eslint-disable-next-line no-unused-expressions
        panel.offsetHeight; // reflow
        panel.style.opacity = '';
      }
    });
  }

  disconnectedCallback() {
    this._tabs?.forEach((tab) => {
      tab.removeEventListener('click', this._onTabClick);
    });
  }
}

/* --------------------------------------------------------------------------
   Init – bootstrap all modules on DOMContentLoaded
   -------------------------------------------------------------------------- */
function init() {
  initScrollReveal();
  initHeaderScroll();
  initHeroParallax();
  initCountUp();

  if (!customElements.get('condition-matcher-component')) {
    customElements.define('condition-matcher-component', ConditionMatcherComponent);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
